import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Search, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
console.log('AdminProductsNew - BACKEND_URL:', BACKEND_URL);
console.log('AdminProductsNew - API:', API);

// Resizable Column Header Component
const ResizableColumnHeader = ({ children, columnKey, width, onResize }) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    setStartX(e.pageX);
    setStartWidth(width);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const diff = e.pageX - startX;
      const newWidth = startWidth + diff;
      onResize(columnKey, newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, startX, startWidth, columnKey, onResize]);

  return (
    <th 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap relative"
      style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
    >
      {children}
      <div
        onMouseDown={handleMouseDown}
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 hover:w-2 transition-all"
        style={{ zIndex: 10 }}
      />
    </th>
  );
};

const AdminProductsNew = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState(false); // true = view, false = edit
  const [editingProduct, setEditingProduct] = useState(null);
  const [cellModal, setCellModal] = useState({ show: false, content: '', label: '' });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [limit] = useState(100);
  
  // Filter states
  const [filters, setFilters] = useState({
    supplier: '',
    category: '',
    gamme: '',
    code1: '',
    code2: '',
    code3: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    suppliers: [],
    categories: [],
    gammes: [],
    code1: [],
    code2: [],
    code3: []
  });
  
  // Column widths state - default widths in pixels
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('admin_products_column_widths');
    return saved ? JSON.parse(saved) : {
      actions: 100,
      id: 150,
      order: 80,
      full_code: 100,
      ref_frs: 100,
      supplier: 120,
      category: 120,
      gamme: 150,
      code1: 150,
      code2: 100,
      code3: 100,
      characteristics: 200,
      subcat1: 100,
      subcat2: 100,
      subcat3: 100
    };
  });
  
  const [formData, setFormData] = useState({
    order: '',
    full_code: '',
    ref_frs: '',
    supplier: '',
    category: '',
    gamme: '',
    code1: '',
    code2: '',
    code3: '',
    characteristics: '',
    subcat1: '',
    subcat2: '',
    subcat3: '',
    acrylique: '',
    teinte_masse: '',
    auto_nettoyant: '',
    deperlant: '',
    anti_fongique: '',
    etanche: '',
    waterproof: '',
    warranty: '',
    width: '',
    motif: '',
    weight: '',
    epaisseur: '',
    protection_uv: '',
    uv: '',
    conform_termique: '',
    transmission_solaire: '',
    reflexion_solaire: '',
    absorption_solaire: '',
    protection_visuelle: '',
    visuel: '',
    gtot_exterieur: '',
    gtot_interieur: '',
    taux_ouverture: '',
    ral: '',
    thumbnail: '',
    image: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchProducts();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    // Debounce search and filters - attendre 500ms après la dernière modification
    const delayFetch = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, searchQuery, filters);
    }, 300); // Réduit à 300ms pour une meilleure réactivité
    
    return () => clearTimeout(delayFetch);
  }, [searchQuery, filters]);

  // Save column widths to localStorage when they change
  useEffect(() => {
    localStorage.setItem('admin_products_column_widths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const handleColumnResize = (columnName, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnName]: Math.max(50, newWidth) // Minimum width of 50px
    }));
  };

  const fetchProducts = async (page = 1, search = '', currentFilters = filters) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (search) {
        params.append('search', search);
      }
      
      // Add filter parameters
      if (currentFilters.supplier) {
        params.append('supplier', currentFilters.supplier);
      }
      if (currentFilters.category) {
        params.append('category', currentFilters.category);
      }
      if (currentFilters.gamme) {
        params.append('gamme', currentFilters.gamme);
      }
      if (currentFilters.code1) {
        params.append('code1', currentFilters.code1);
      }
      if (currentFilters.code2) {
        params.append('code2', currentFilters.code2);
      }
      if (currentFilters.code3) {
        params.append('code3', currentFilters.code3);
      }
      
      // Récupérer les produits paginés
      const productsResponse = await axios.get(`${API}/admin/products/?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // L'API retourne { items: [...], total: count, page: current_page, pages: total_pages }
      const productsData = productsResponse.data.items || [];
      setProducts(productsData);
      setFilteredProducts(productsData);
      setTotalPages(productsResponse.data.pages || 1);
      setTotalProducts(productsResponse.data.total || 0);
      setCurrentPage(productsResponse.data.page || 1);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/products/filters/options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      supplier: '',
      category: '',
      gamme: '',
      code1: '',
      code2: '',
      code3: ''
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(v => v !== '').length;
  };

  const handleView = (product) => {
    setEditingProduct(product);
    setFormData({
      order: product.order || '',
      full_code: product.full_code || '',
      ref_frs: product.ref_frs || '',
      supplier: product.supplier || '',
      category: product.category || '',
      gamme: product.gamme || '',
      code1: product.code1 || '',
      code2: product.code2 || '',
      code3: product.code3 || '',
      characteristics: product.characteristics || '',
      subcat1: product.subcat1 || '',
      subcat2: product.subcat2 || '',
      subcat3: product.subcat3 || '',
      acrylique: product.acrylique || '',
      teinte_masse: product.teinte_masse || '',
      auto_nettoyant: product.auto_nettoyant || '',
      deperlant: product.deperlant || '',
      anti_fongique: product.anti_fongique || '',
      etanche: product.etanche || '',
      waterproof: product.waterproof || '',
      warranty: product.warranty || '',
      width: product.width || '',
      motif: product.motif || '',
      weight: product.weight || '',
      epaisseur: product.epaisseur || '',
      protection_uv: product.protection_uv || '',
      uv: product.uv || '',
      conform_termique: product.conform_termique || '',
      transmission_solaire: product.transmission_solaire || '',
      reflexion_solaire: product.reflexion_solaire || '',
      absorption_solaire: product.absorption_solaire || '',
      protection_visuelle: product.protection_visuelle || '',
      visuel: product.visuel || '',
      gtot_exterieur: product.gtot_exterieur || '',
      gtot_interieur: product.gtot_interieur || '',
      taux_ouverture: product.taux_ouverture || '',
      ral: product.ral || '',
      thumbnail: product.thumbnail || '',
      image: product.image || ''
    });
    setViewMode(true);
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      order: product.order || '',
      full_code: product.full_code || '',
      ref_frs: product.ref_frs || '',
      supplier: product.supplier || '',
      category: product.category || '',
      gamme: product.gamme || '',
      code1: product.code1 || '',
      code2: product.code2 || '',
      code3: product.code3 || '',
      characteristics: product.characteristics || '',
      subcat1: product.subcat1 || '',
      subcat2: product.subcat2 || '',
      subcat3: product.subcat3 || '',
      acrylique: product.acrylique || '',
      teinte_masse: product.teinte_masse || '',
      auto_nettoyant: product.auto_nettoyant || '',
      deperlant: product.deperlant || '',
      anti_fongique: product.anti_fongique || '',
      etanche: product.etanche || '',
      waterproof: product.waterproof || '',
      warranty: product.warranty || '',
      width: product.width || '',
      motif: product.motif || '',
      weight: product.weight || '',
      epaisseur: product.epaisseur || '',
      protection_uv: product.protection_uv || '',
      uv: product.uv || '',
      conform_termique: product.conform_termique || '',
      transmission_solaire: product.transmission_solaire || '',
      reflexion_solaire: product.reflexion_solaire || '',
      absorption_solaire: product.absorption_solaire || '',
      protection_visuelle: product.protection_visuelle || '',
      visuel: product.visuel || '',
      gtot_exterieur: product.gtot_exterieur || '',
      gtot_interieur: product.gtot_interieur || '',
      taux_ouverture: product.taux_ouverture || '',
      ral: product.ral || '',
      thumbnail: product.thumbnail || '',
      image: product.image || ''
    });
    setViewMode(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const data = {
        ...formData,
        order: formData.order ? parseInt(formData.order) : null
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}/`, data, { headers });
      } else {
        await axios.post(`${API}/admin/products/`, data, { headers });
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Erreur lors de la sauvegarde du produit');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      console.log('Deleting product with fetch:', productId);
      console.log('Delete URL:', `${BACKEND_URL}/api/admin/products/${productId}`);
      
      // Use native fetch with absolute URL to avoid platform script interception
      const response = await fetch(`${BACKEND_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la suppression du produit');
      }
      
      const data = await response.json();
      console.log('Delete response data:', data);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert(error.message || 'Erreur lors de la suppression du produit');
    }
  };

  const resetForm = () => {
    setFormData({
      order: '',
      full_code: '',
      ref_frs: '',
      supplier: '',
      category: '',
      gamme: '',
      code1: '',
      code2: '',
      code3: '',
      characteristics: '',
      subcat1: '',
      subcat2: '',
      subcat3: '',
      acrylique: '',
      teinte_masse: '',
      auto_nettoyant: '',
      deperlant: '',
      anti_fongique: '',
      etanche: '',
      waterproof: '',
      warranty: '',
      width: '',
      motif: '',
      weight: '',
      epaisseur: '',
      protection_uv: '',
      uv: '',
      conform_termique: '',
      transmission_solaire: '',
      reflexion_solaire: '',
      absorption_solaire: '',
      protection_visuelle: '',
      visuel: '',
      gtot_exterieur: '',
      gtot_interieur: '',
      taux_ouverture: '',
      ral: '',
      thumbnail: '',
      image: ''
    });
  };

  const handleCellClick = (content, label) => {
    if (content && content !== '-' && content.length > 20) {
      setCellModal({ show: true, content, label });
    }
  };

  const handleAddNew = () => {
    resetForm();
    setEditingProduct(null);
    setViewMode(false);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion des produits</h1>
                <p className="text-sm text-gray-600">
                  {totalProducts > 0 ? (
                    getActiveFiltersCount() > 0 || searchQuery ? (
                      <span><span className="font-semibold">{totalProducts}</span> produits trouvés</span>
                    ) : (
                      <span><span className="font-semibold">{totalProducts}</span> produits au total</span>
                    )
                  ) : (
                    'Aucun produit'
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-celadon text-white rounded hover:bg-celadon-light transition-colors"
            >
              <Plus size={20} />
              Ajouter un produit
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
            />
          </div>

          {/* Filters Row */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Filtres</h3>
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                >
                  Réinitialiser tous les filtres ({getActiveFiltersCount()})
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Supplier Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fournisseur</label>
                <select
                  value={filters.supplier}
                  onChange={(e) => handleFilterChange('supplier', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-celadon"
                >
                  <option value="">Tous</option>
                  {filterOptions.suppliers.map(supplier => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie</label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-celadon"
                >
                  <option value="">Toutes</option>
                  {filterOptions.categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Gamme Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Collection</label>
                <select
                  value={filters.gamme}
                  onChange={(e) => handleFilterChange('gamme', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-celadon"
                >
                  <option value="">Toutes</option>
                  {filterOptions.gammes.map(gamme => (
                    <option key={gamme} value={gamme}>{gamme}</option>
                  ))}
                </select>
              </div>

              {/* Code1 Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Code1</label>
                <select
                  value={filters.code1}
                  onChange={(e) => handleFilterChange('code1', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-celadon"
                >
                  <option value="">Tous</option>
                  {filterOptions.code1.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>

              {/* Code2 Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Code2</label>
                <select
                  value={filters.code2}
                  onChange={(e) => handleFilterChange('code2', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-celadon"
                >
                  <option value="">Tous</option>
                  {filterOptions.code2.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>

              {/* Code3 Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Code3</label>
                <select
                  value={filters.code3}
                  onChange={(e) => handleFilterChange('code3', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-celadon"
                >
                  <option value="">Tous</option>
                  {filterOptions.code3.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active Filters Display */}
            {getActiveFiltersCount() > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {filters.supplier && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Fournisseur: {filters.supplier}
                    <button onClick={() => handleFilterChange('supplier', '')} className="hover:text-blue-900">×</button>
                  </span>
                )}
                {filters.category && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Catégorie: {filters.category}
                    <button onClick={() => handleFilterChange('category', '')} className="hover:text-green-900">×</button>
                  </span>
                )}
                {filters.gamme && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                    Collection: {filters.gamme}
                    <button onClick={() => handleFilterChange('gamme', '')} className="hover:text-purple-900">×</button>
                  </span>
                )}
                {filters.code1 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    Code1: {filters.code1}
                    <button onClick={() => handleFilterChange('code1', '')} className="hover:text-yellow-900">×</button>
                  </span>
                )}
                {filters.code2 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-800 text-xs rounded-full">
                    Code2: {filters.code2}
                    <button onClick={() => handleFilterChange('code2', '')} className="hover:text-pink-900">×</button>
                  </span>
                )}
                {filters.code3 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">
                    Code3: {filters.code3}
                    <button onClick={() => handleFilterChange('code3', '')} className="hover:text-indigo-900">×</button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Products Table - TOUS LES CHAMPS */}
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full table-fixed" style={{minWidth: '4000px'}}>
            <thead className="bg-gray-50 border-b sticky top-0">
              <tr>
                <ResizableColumnHeader columnKey="actions" width={columnWidths.actions} onResize={handleColumnResize}>
                  Actions
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="id" width={columnWidths.id} onResize={handleColumnResize}>
                  ID
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="order" width={columnWidths.order} onResize={handleColumnResize}>
                  Order
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="full_code" width={columnWidths.full_code} onResize={handleColumnResize}>
                  Full Code
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="ref_frs" width={columnWidths.ref_frs} onResize={handleColumnResize}>
                  Ref-FRS
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="supplier" width={columnWidths.supplier} onResize={handleColumnResize}>
                  Supplier
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="category" width={columnWidths.category} onResize={handleColumnResize}>
                  Category
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="gamme" width={columnWidths.gamme} onResize={handleColumnResize}>
                  Collection
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="code1" width={columnWidths.code1} onResize={handleColumnResize}>
                  Code1
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="code2" width={columnWidths.code2} onResize={handleColumnResize}>
                  Code2
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="code3" width={columnWidths.code3} onResize={handleColumnResize}>
                  Code3
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="characteristics" width={columnWidths.characteristics} onResize={handleColumnResize}>
                  Characteristics
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="subcat1" width={columnWidths.subcat1} onResize={handleColumnResize}>
                  Subcat1
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="subcat2" width={columnWidths.subcat2} onResize={handleColumnResize}>
                  Subcat2
                </ResizableColumnHeader>
                <ResizableColumnHeader columnKey="subcat3" width={columnWidths.subcat3} onResize={handleColumnResize}>
                  Subcat3
                </ResizableColumnHeader>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Acrylique</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Teinte Masse</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Auto-nettoyant</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Déperlant</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Anti-fongique</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Étanche</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Waterproof</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Warranty</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Width</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Motif</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Weight</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Épaisseur</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Protection UV</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">UV</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Conform Termique</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Transmission Solaire</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Réflexion Solaire</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Absorption Solaire</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Protection Visuelle</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Visuel</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Gtot Extérieur</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Gtot Intérieur</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Taux Ouverture</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">RAL</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Thumbnail</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Image</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(product)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Modifier"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.id, 'ID')}>{product.id}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.order, 'Order')}>{product.order || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.full_code, 'Full Code')}>{product.full_code || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.ref_frs, 'Ref-FRS')}>{product.ref_frs || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 font-medium cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.supplier, 'Supplier')}>{product.supplier || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.category, 'Category')}>{product.category || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 font-medium cursor-pointer hover:bg-gray-100" style={{maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.gamme, 'Collection')} title={product.gamme || '-'}>{product.gamme || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.code1, 'Code1')}>{product.code1 || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.code2, 'Code2')}>{product.code2 || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 font-medium cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.code3, 'Code3')}>{product.code3 || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.characteristics, 'Characteristics')}>{product.characteristics || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.subcat1, 'Subcat1')}>{product.subcat1 || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.subcat2, 'Subcat2')}>{product.subcat2 || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.subcat3, 'Subcat3')}>{product.subcat3 || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.acrylique, 'Acrylique')}>{product.acrylique || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.teinte_masse, 'Teinte Masse')}>{product.teinte_masse || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.auto_nettoyant, 'Auto-nettoyant')}>{product.auto_nettoyant || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.deperlant, 'Déperlant')}>{product.deperlant || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.anti_fongique, 'Anti-fongique')}>{product.anti_fongique || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.etanche, 'Étanche')}>{product.etanche || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.waterproof, 'Waterproof')}>{product.waterproof || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.warranty, 'Warranty')}>{product.warranty || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.width, 'Width')}>{product.width || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.motif, 'Motif')}>{product.motif || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.weight, 'Weight')}>{product.weight || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.epaisseur, 'Épaisseur')}>{product.epaisseur || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.protection_uv, 'Protection UV')}>{product.protection_uv || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.uv, 'UV')}>{product.uv || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.conform_termique, 'Conform Termique')}>{product.conform_termique || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.transmission_solaire, 'Transmission Solaire')}>{product.transmission_solaire || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.reflexion_solaire, 'Réflexion Solaire')}>{product.reflexion_solaire || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.absorption_solaire, 'Absorption Solaire')}>{product.absorption_solaire || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.protection_visuelle, 'Protection Visuelle')}>{product.protection_visuelle || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.visuel, 'Visuel')}>{product.visuel || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.gtot_exterieur, 'Gtot Extérieur')}>{product.gtot_exterieur || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.gtot_interieur, 'Gtot Intérieur')}>{product.gtot_interieur || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.taux_ouverture, 'Taux Ouverture')}>{product.taux_ouverture || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.ral, 'RAL')}>{product.ral || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.thumbnail, 'Thumbnail')}>{product.thumbnail || '-'}</td>
                  <td className="px-3 py-3 text-xs text-gray-900 cursor-pointer hover:bg-gray-100" style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} onClick={() => handleCellClick(product.image, 'Image')}>{product.image || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination Controls */}
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Affichage de <span className="font-medium">{filteredProducts.length}</span> sur{' '}
              <span className="font-medium">{totalProducts}</span> produits
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                    fetchProducts(currentPage - 1, searchQuery, filters);
                  }
                }}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-700 px-4">
                Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span>
              </span>
              <button
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                    fetchProducts(currentPage + 1, searchQuery, filters);
                  }
                }}
                disabled={currentPage >= totalPages || loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal contenu cellule */}
      {cellModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setCellModal({ show: false, content: '', label: '' })}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3">{cellModal.label}</h3>
            <div className="bg-gray-50 p-4 rounded border border-gray-200 max-h-96 overflow-y-auto">
              <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{cellModal.content}</p>
            </div>
            <button
              onClick={() => setCellModal({ show: false, content: '', label: '' })}
              className="mt-4 w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Modal TOUS LES CHAMPS */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-6xl w-full my-8">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold">
                {viewMode ? 'Détails du produit' : (editingProduct ? 'Modifier le produit' : 'Ajouter un produit')}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto">
                {/* Tous les champs Excel */}
                {Object.keys(formData).map((key) => (
                  <div key={key}>
                    <label className="block text-sm font-medium mb-1 capitalize">
                      {key.replace(/_/g, ' ')}
                    </label>
                    {key === 'characteristics' || key === 'subcat2' ? (
                      <textarea
                        value={formData[key]}
                        onChange={(e) => !viewMode && setFormData({ ...formData, [key]: e.target.value })}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:border-celadon text-sm"
                        rows="3"
                        disabled={viewMode}
                      />
                    ) : (
                      <input
                        type="text"
                        value={formData[key]}
                        onChange={(e) => !viewMode && setFormData({ ...formData, [key]: e.target.value })}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:border-celadon text-sm"
                        disabled={viewMode}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  {viewMode ? 'Fermer' : 'Annuler'}
                </button>
                {!viewMode && (
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-celadon text-white rounded hover:bg-celadon-light transition-colors"
                  >
                    {editingProduct ? 'Modifier' : 'Ajouter'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsNew;
